import React, {forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import {Button, CircularProgress, Grid, Paper, Typography} from '@mui/material';
import {useStylesECDS} from './ecds/ecdsStyle';
import {useTranslation} from 'react-i18next';
import DeleteIcon from '@mui/icons-material/Delete';
import _ from 'lodash';
import Box from '@mui/material/Box';
import moment from 'moment';
import {useDispatch, useSelector} from 'react-redux';
import {hasPermission, Permissions, user} from '../../../store/reducers/user';
import uuidv4 from 'uuid/v4';
import {additionalData, updatePatientAdditionalData} from '../../../store/reducers/additionalData';
import {addNoteForPatient} from '../../../store/reducers/notes';
import MessageBox from '../../../util/messageBox';
import Notes from '../../../util/notes';
import Formatter from '../../../util/formatter';
import InformationBox from '../../../components/other/informationBox';
import AgyleButton from '../../../components/ui/buttons/agyleButton';
import AgyleTextField from '../../../components/ui/fields/agyleTextField';
import AgyleBlockSwitch from '../../../components/ui/switch/agyleBlockSwitch';
import useOpenState from '../../../hooks/useOpenState';
import {AgyleTable, createAgyleTableColumn} from '../../../components/table/AgyleTable';

const Household = (props) => {
    const {t} = useTranslation();
    const childRef = useRef();
    const dispatch = useDispatch();

    const [state, setState] = useState({current: null, previous: null});

    useEffect(() => {
        const button = [
            <AgyleButton
                disabled={!dispatch(hasPermission(Permissions.AGYLE_CAN_MANAGE_HOUSEHOLD))}
                variant={props._isFullFormMode ? 'outlined' : 'contained'}
                colour={props._isFullFormMode ? 'primary' : 'info'}
                onClick={() => childRef.current.newHousehold()}
                key="new-household"
            >
                {t('Add New Household')}
            </AgyleButton>,
        ];
        if (props.button) {
            props.button(button);
        }
        if (props.onSubmitFunction) {
            props.onSubmitFunction(handleSubmit);
        }
        if (props.onCustomButtons) {
            props.onCustomButtons(button);
        }
    }, [state]);

    const handleSubmit = () => {
        const data = {'household': state?.current};
        dispatch(updatePatientAdditionalData(props.patient.patient.__id, data));
        dispatch(addNoteForPatient(props.patient.patient.__id, {
            note: Notes.getHouseholdNote(state?.previous, state?.current),
            noteType: 'household',
            patientsInWardsRef: [props.patient.__id],
            episodeId: props.patient?.admission?.consultantEpisodeId || null,
        }, props.patient.__id));
    };

    return (
        <Grid container direction="row" justifyContent="flex-start" alignItems="center">
            <Grid item xs={12}>
                <InformationBox type="info">
                    {t('Record the following information')}
                    <ul>
                        <li>{t('Please state who else lives in the household with the child?')}</li>
                        <li>{t('Under 18s - Please state Name, Age and relationship to the child.')}</li>
                        <li>{t('Over 18s - Please state Name and relationship to the child.')}</li>
                    </ul>
                </InformationBox>
            </Grid>

            <Grid item xs={12}>
                <HouseholdForm
                    ref={childRef}
                    patient={props.patient}
                    width={props?._containerWidth}
                    setData={setState}
                />

                {(!props.button && !props._isFullFormMode) &&
                    <AgyleButton
                        variant="contained"
                        color="primary"
                        disabled={!dispatch(hasPermission(Permissions.AGYLE_CAN_MANAGE_HOUSEHOLD))}
                        style={{margin: '10px', width: 'auto', float: 'right'}}
                        onClick={() => childRef.current.newHousehold()}
                    >
                        {t('Add New Household')}
                    </AgyleButton>
                }
            </Grid>
        </Grid>
    );
};

export default Household;

// To use in other form
const HouseholdForm = forwardRef((props, ref) => {
    const classes = useStylesECDS();
    const {t} = useTranslation();
    const userObject = useSelector(user);
    const patientAddData = useSelector(additionalData);

    const firstUpdate = useRef(true);

    const timestamp = moment().toISOString();
    const tester = Formatter.formatUserFullName(userObject);

    //Init data from core ds
    const [mainHousehold, setMainHousehold] = useState([]);
    const [firstVersion, setFirstVersion] = useState([]);

    const [loading, setLoading] = useState(false);

    useImperativeHandle(ref, () => ({
        newHousehold() {
            addNewHousehold();
        },
    }));

    useEffect(() => {
        setLoading(true);
        if (patientAddData && patientAddData.other && patientAddData.other.household) {
            setMainHousehold(patientAddData.other.household);
            setFirstVersion(patientAddData.other.household);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        if (props?.setData && !_.isEqual(mainHousehold, firstVersion)) {
            props.setData({current: mainHousehold, previous: firstVersion});
        }
    }, [mainHousehold]);

    // Add an empty household
    const addNewHousehold = () => {
        const emptyObj = {
            id: uuidv4() + '',
            householdInformation: '',
            householdPeople: [],
            date: timestamp,
            by: tester,
            login: userObject?.userName,
        };
        firstUpdate.current = false;
        setMainHousehold(prevState => [...prevState, emptyObj]);
    };

    const saveData = (data) => {
        let array = _.cloneDeep(mainHousehold);
        const index = _.findIndex(array, {id: data.id});
        array.splice(index, 1, data);

        if (!_.isEqual(array, mainHousehold)) {
            firstUpdate.current = false;
            setMainHousehold(array);
        }
    };

    const deleteHousehold = (deleteHousehold) => {
        firstUpdate.current = false;
        setMainHousehold(prevState => prevState.filter(value => value.id !== deleteHousehold.id));
    };

    const renderNewHousehold = (description) => {
        return (
            <Paper className={classes.paper} elevation={3}>
                <Grid container direction="row" justifyContent="flex-start" alignItems="center">
                    <Grid item xs={8}>
                        {description &&
                            <h3 className={classes.description}>
                                {t('No household information was provided, please add a new household')}
                            </h3>}
                    </Grid>
                </Grid>
            </Paper>
        );
    };

    return (
        <>
            {!loading && (mainHousehold && mainHousehold.length === 0) && renderNewHousehold(true)}
            {loading && (
                <Grid container direction="column" justifyContent="center" alignItems="center">
                    <Box stysx={{display: 'flex'}}>
                        <CircularProgress/>
                    </Box>
                </Grid>
            )}
            {mainHousehold.map((element, index) => {
                return (
                    <div key={element.id}>
                        <HouseholdComponent
                            disableSave={props.disableSave}
                            data={element}
                            index={index}
                            save={saveData}
                            delete={deleteHousehold}
                            width={props.width}
                        />
                    </div>
                );
            })}
            {(mainHousehold && mainHousehold.length !== 0) && renderNewHousehold(false)}
        </>
    );
});

const HouseholdComponent = (props) => {
    const classes = useStylesECDS();
    const {t} = useTranslation();
    const userObject = useSelector(user);
    const dispatch = useDispatch();

    //  For Delete
    const [deleteItem, setDeleteItem] = useState(null);
    const [isDeleteConfirmOpen, openDeleteConfirm, closeDeleteConfirm] = useOpenState(false, true);
    const [isDeleteHouseholdOpen, openDeleteHousehold, closeDeleteHousehold] = useOpenState(false, true);

    const tester = Formatter.formatUserFullName(userObject);

    const [isSmall, setIsSmall] = useState(false);

    // Data form
    const [householdComponent, setHouseholdComponent] = useState(props.data);
    const [form, setForm] = useState({
        parentalResponsibility: false,
        name: '',
        age: '',
        relationship: '',
        contact: '',
    });
    const [error, setError] = useState(false);

    useEffect(() => {
        props.save(householdComponent);
    }, [householdComponent]);

    useEffect(() => {
        setIsSmall(props.width < 730);
    }, [props.width]);

    const deletePeople = () => {
        let householdComp = _.cloneDeep(householdComponent);
        householdComp.householdPeople = householdComp.householdPeople.filter(v => v.id !== deleteItem.id);
        setHouseholdComponent(householdComp);

        setDeleteItem(null);
        closeDeleteConfirm();
    };

    const deleteHousehold = () => {
        props.delete(deleteItem);
        setDeleteItem(null);
        closeDeleteHousehold();
    };

    const handleChange = (event) => {
        const {name, value} = event.target;

        setForm(prevState => ({...prevState, [name]: value}));
    };

    const checkboxHandler = (name, value) => {
        setForm(prevState => ({...prevState, [name]: value}));
    };

    const addToTable = () => {
        const error = form.name !== '' && form.relationship !== '';
        if (!error) {
            setError(true);
        } else {
            const dataForm = {
                ...form,
                id: uuidv4() + '',
                date: moment().toISOString(),
                by: tester,
                login: userObject?.userName,
            };

            if (typeof householdComponent.householdPeople === 'undefined') {
                setHouseholdComponent(prevState => ({...prevState, householdPeople: [dataForm]}));
            } else {
                setHouseholdComponent(prevState => ({
                    ...prevState,
                    householdPeople: [...prevState.householdPeople, dataForm],
                }));
            }
            setForm({
                parentalResponsibility: false,
                name: '',
                age: '',
                relationship: '',
                contact: '',
            });
            setError(false);
        }
    };

    const renderTable = useMemo(() => {
        if (householdComponent && householdComponent.householdPeople) {
            const columns = [
                createAgyleTableColumn('count', t('No'), 1, null, null, 'notSort', 'medium', 'center', false, false),
                createAgyleTableColumn('name', t('Person Name'), 2, 'name', 'string', 'notSort', 'medium', 'center', false, false),
                createAgyleTableColumn('parentalResponsibility', t('Parental Responsibility'), 3, 'parentalResponsibility', 'booleanCheckbox', 'notSort', 'medium', 'center', false, false),
                createAgyleTableColumn('age', t('Age'), 4, 'age', 'string', 'notSort', 'medium', 'medium', false, false),
                createAgyleTableColumn('relationship', t('Relationship'), 5, 'relationship', 'string', 'notSort', 'medium', 'center', false, false),
                createAgyleTableColumn('contact', t('Contact Details'), 6, 'contact', 'string', 'notSort', 'medium', 'center', false, false),
                createAgyleTableColumn('delete', 'Action', 8, null, 'action', '', 'medium', 'right', true, false, null, null, null),
            ];

            return (
                <Grid item xs={12}>
                    <AgyleTable
                        columns={columns}
                        data={householdComponent?.householdPeople || []}
                        deleteItem={(row) => {
                            setDeleteItem(row);
                            openDeleteConfirm();
                        }}
                        disabledAction={props.disableSave || !dispatch(hasPermission(Permissions.AGYLE_CAN_MANAGE_HOUSEHOLD))}
                        disableFilter
                    />
                </Grid>
            );
        }
    }, [householdComponent]);

    const renderHousehold = () => {
        return (
            <Paper className={classes.paper} elevation={3}>
                <Grid container direction="row" justifyContent="space-between" alignItems="center">
                    <span style={{margin: '10px'}}> {t('Household ')} {props.index + 1}
                        <Button
                            disabled={props.disableSave || !dispatch(hasPermission(Permissions.AGYLE_CAN_MANAGE_HOUSEHOLD))}
                            onClick={() => {
                                setDeleteItem(householdComponent);
                                openDeleteHousehold();
                            }}>
                            <DeleteIcon/>
                        </Button>
                    </span>

                    <AgyleButton
                        variant="contained"
                        color="primary"
                        className={classes.button}
                        onClick={addToTable}
                        disabled={props.disableSave || !dispatch(hasPermission(Permissions.AGYLE_CAN_MANAGE_HOUSEHOLD))}
                    >
                        {t('Add')}
                    </AgyleButton>
                </Grid>

                <Grid container spacing={2} direction="row" justifyContent="flex-start" alignItems="center">
                    <Grid item xs={isSmall ? 12 : 5}>
                        <AgyleTextField
                            fullWidth
                            name="name"
                            label={t('Name')}
                            className={classes.input}
                            onChange={handleChange}
                            value={form.name || ''}
                            error={error}
                            size="large"
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>

                    <Grid item xs={isSmall ? 12 : 2}>
                        <AgyleTextField
                            fullWidth
                            name="age"
                            label={t('Age')}
                            className={classes.input}
                            onChange={handleChange}
                            value={form.age || ''}
                            type="number"
                            inputProps={{maxLength: 3}}
                            size="large"
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>

                    <Grid item xs={isSmall ? 12 : 5}>
                        <AgyleTextField
                            fullWidth
                            name="relationship"
                            label={t('Relationship')}
                            className={classes.input}
                            onChange={handleChange}
                            value={form.relationship || ''}
                            error={error}
                            size="large"
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>

                    <Grid item xs={isSmall ? 12 : 3}
                          style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 5,
                          }}
                    >
                        <Typography variant="body2">
                            {t('Parental Responsibility')}
                        </Typography>

                        <AgyleBlockSwitch
                            onChange={(value) => {
                                checkboxHandler('parentalResponsibility', value);
                            }}
                            value={form.parentalResponsibility || false}
                        />
                    </Grid>

                    <Grid item xs={isSmall ? 12 : 9}>
                        <AgyleTextField
                            fullWidth
                            name="contact"
                            label={t('Contact details')}
                            className={classes.input}
                            onChange={handleChange}
                            value={form.contact || ''}
                            variant="outlined"
                            size="large"
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>
                </Grid>

                <Grid
                    container
                    direction="column"
                    justifyContent="flex-start"
                    alignItems="center"
                    style={{marginTop: '10px'}}
                >
                    <Grid container direction="row" justifyContent="flex-end" alignItems="center">
                        <Grid item xs={12}>
                            <AgyleTextField
                                name="householdInformation"
                                fullWidth
                                label={t('Household Additional Information')}
                                className={classes.input}
                                onChange={event => setHouseholdComponent(prevState => ({
                                    ...prevState,
                                    householdInformation: event.target.value,
                                }))}
                                value={householdComponent.householdInformation || ''}
                                variant="outlined"
                                size="large"
                                noBorder={false}
                                withNewBackground
                            />
                        </Grid>
                    </Grid>

                    <div style={{width: '100%'}}>
                        {renderTable}
                    </div>
                </Grid>
            </Paper>
        );
    };

    return (
        <>
            {renderHousehold()}

            <MessageBox
                onClose={closeDeleteConfirm}
                title={t('Confirmation')}
                message={t('Do you really want to remove this item?')}
                haveAccept={{yes: t('Submit'), no: t('Close')}}
                onAccept={deletePeople}
                open={isDeleteConfirmOpen}
            />

            <MessageBox
                onClose={closeDeleteHousehold}
                title={t('Confirmation')}
                message={t('Do you really want to remove this whole household?')}
                haveAccept={{yes: t('Submit'), no: t('Close')}}
                onAccept={deleteHousehold}
                open={isDeleteHouseholdOpen}
            />
        </>
    );
};